import { UserRole, VerificationStatus, type ProfileDetail } from '@teentack/shared';
import type { BadgeTone } from '@/components/ui/Badge';
import {
  DAY_LABELS,
  TIME_SLOT_LABELS,
  WORK_FREQUENCY_LABELS,
  WORK_TYPE_LABELS,
} from '@/components/fields/availability-vocabulary';

/**
 * Presentation helpers for the profile surface. Kept out of the component
 * files so each of those exports only components.
 */

export const ROLE_LABELS: Record<string, string> = {
  [UserRole.TEEN]: 'Teen',
  [UserRole.PARENT]: 'Parent / guardian',
  [UserRole.JOB_POSTER]: 'Job poster',
  [UserRole.ADMIN]: 'Admin',
};

export const VERIFICATION_BADGES: Record<string, { label: string; tone: BadgeTone }> = {
  [VerificationStatus.APPROVED]: { label: 'Verified', tone: 'safe' },
  [VerificationStatus.PENDING]: { label: 'Verification pending', tone: 'sun' },
  [VerificationStatus.REJECTED]: { label: 'Verification rejected', tone: 'neutral' },
};

export function initialsOf(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/** "Austin, TX" — whichever parts of the address are filled in. */
export function locationLabel(profile: ProfileDetail): string | null {
  const parts = [profile.city, profile.state].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** "Sat, Sun · Mornings" — the availability column in one readable line. */
export function availabilityLine(profile: ProfileDetail): string | null {
  const days = profile.availability?.days?.map((day) => DAY_LABELS[day]).join(', ');
  const slots = profile.availability?.timeSlots?.map((slot) => TIME_SLOT_LABELS[slot]).join(', ');
  const parts = [days, slots].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function workLine(profile: ProfileDetail): string | null {
  const types = profile.workPreferences?.type?.map((type) => WORK_TYPE_LABELS[type]).join(', ');
  const frequency = profile.workPreferences?.frequency;
  const parts = [types, frequency ? WORK_FREQUENCY_LABELS[frequency] : null].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export interface Requirement {
  label: string;
  done: boolean;
}

/**
 * What is still missing before the profile is worth showing to a job poster.
 * The list is role-aware — a parent is never asked for skills.
 */
export function requirementsFor(profile: ProfileDetail): Requirement[] {
  const shared: Requirement[] = [
    { label: 'Name', done: Boolean(profile.firstName && profile.lastName) },
    { label: 'Profile photo', done: Boolean(profile.photoUrl) },
    { label: 'City and state', done: Boolean(profile.city && profile.state) },
    { label: 'ZIP code', done: Boolean(profile.zipCode) },
  ];

  if (profile.role === UserRole.TEEN) {
    return [
      ...shared,
      { label: 'Short bio', done: Boolean(profile.bio) },
      { label: 'At least one skill', done: profile.skills.length > 0 },
      { label: 'Availability', done: Boolean(profile.availability?.days?.length) },
      { label: 'Work preferences', done: Boolean(profile.workPreferences?.type?.length) },
    ];
  }

  if (profile.role === UserRole.JOB_POSTER) {
    return [
      ...shared,
      { label: 'Professional title', done: Boolean(profile.professionalTitle) },
      { label: 'About your work', done: Boolean(profile.bio) },
      { label: 'Street address', done: Boolean(profile.streetAddress) },
    ];
  }

  return shared;
}
